import { useEffect, useRef, useState } from 'react'

import { getSilentToken } from '../../../services/tokenUtils'
import {
    fetchUserAccountData,
    submitAccountSelection,
    UserAccountData,
} from '../service/CopUserAccountService'

const TOKEN_ACQUIRED_EVENT = 'cop:token-acquired'
const waitForToken = (): Promise<string | null> => {
    const existing = getSilentToken()
    if (existing) return Promise.resolve(existing)

    return new Promise<string | null>((resolve) => {
        document.addEventListener(
            TOKEN_ACQUIRED_EVENT,
            (e: Event) => {
                const detail = (e as CustomEvent<{ accessToken?: string }>).detail
                resolve(detail?.accessToken ?? null)
            },
            { once: true },
        )
    })
}

const isAuthorMode = (): boolean => {
    try {
        const w = window as unknown as { Granite?: { author?: unknown } }
        return Boolean(w.Granite?.author)
    } catch {
        return false
    }
}

export type UseCopUserAccountReturn = {
    data: UserAccountData | null
    loading: boolean
    searching: boolean
    loadingMore: boolean
    hasMore: boolean
    error: boolean
    selectedId: string
    setSelectedId: (id: string) => void
    submitting: boolean
    confirm: () => Promise<boolean>
    setSearchTerm: (term: string) => void
    loadMore: () => void
}

export function useCopUserAccount(selectionApiUrl: string): UseCopUserAccountReturn {
    const [data, setData] = useState<UserAccountData | null>(null)
    const [loading, setLoading] = useState(true)
    const [searching, setSearching] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [skip, setSkip] = useState(0)
    const [error, setError] = useState(false)
    const [selectedId, setSelectedId] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const isInitialLoad = useRef(true)

    useEffect(() => {
        if (isAuthorMode()) {
            setLoading(false)
            return
        }
        let cancelled = false
        const initialLoad = isInitialLoad.current
        isInitialLoad.current = false

        const timer = setTimeout(async () => {
            if (initialLoad) {
                setLoading(true)
                setError(false)
                await waitForToken()
            } else {
                setSearching(true)
                setSkip(0)
            }
            if (cancelled) return
            const result = await fetchUserAccountData(searchTerm || undefined, 0)
            if (cancelled) return
            setData(result)
            setHasMore((result?.value?.length ?? 0) === 50)
            if (!result) setError(true)
            if (initialLoad) { setLoading(false) } else { setSearching(false) }
        }, initialLoad || !searchTerm ? 0 : 300)

        return () => { cancelled = true; clearTimeout(timer) }
    }, [searchTerm])

    const loadMore = async () => {
        if (!hasMore || loadingMore || searching || loading || isAuthorMode()) return
        const nextSkip = skip + 50
        setSkip(nextSkip)
        setLoadingMore(true)
        const result = await fetchUserAccountData(searchTerm || undefined, nextSkip)
        setLoadingMore(false)
        if (!result) return
        setHasMore(result.value.length === 50)
        setData(prev => prev ? { ...prev, value: [...prev.value, ...result.value] } : result)
    }

    const confirm = async (): Promise<boolean> => {
        if (!selectedId) return false
        setSubmitting(true)

        const ok = await submitAccountSelection(selectionApiUrl, selectedId)
        setSubmitting(false)
        return ok
    }

    return { data, loading, searching, loadingMore, hasMore, error, selectedId, setSelectedId, submitting, confirm, setSearchTerm, loadMore }
}
